<template>
    <div>
        <w-app
            :title="$t('module.planning')"
            show-filter
            :filter="filter"
            @clearFilter="clearFilter"
        >
            <template v-slot:filter>
                <absence-request-filter v-model="filter" hide-date/>
            </template>
            <template v-slot:leftToolbar>
                <v-btn
                    outlined
                    class="ml-2"
                    small
                    color="grey darken-2"
                    @click="setToday"
                >
                    {{ $t('common.today') }}
                </v-btn>
                <v-btn
                    icon
                    color="grey darken-2"
                    @click="prev"
                >
                    <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn
                    icon
                    color="grey darken-2"
                    @click="next"
                >
                    <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
                <strong v-if="$refs.calendar">{{ $refs.calendar.title.toUpperCase() }}</strong>
            </template>
            <template v-slot:rightToolbar>
                <v-btn-toggle
                    v-model="type"
                    tile
                    color="teal"
                    group
                >
                    <v-btn value="day" small>{{ typeToLabel['day'] }}</v-btn>

                    <v-btn value="week" small>{{ typeToLabel['week'] }}</v-btn>

                    <v-btn value="month" small>{{ typeToLabel['month'] }}</v-btn>

                    <v-btn value="4day" small>{{ typeToLabel['4day'] }}</v-btn>
                </v-btn-toggle>
            </template>
            <template v-slot:content>
                <v-row class="fill-height">
                    <v-col>
                        <v-sheet height="800">
                            <v-skeleton-loader
                                v-show="loading"
                                ref="skeleton"
                                :type="'table'"
                                :class="`mx-auto elevation-${tableElevation}`"
                            ></v-skeleton-loader>
                            <v-calendar
                                ref="calendar"
                                v-show="!loading"
                                :class="`elevation-${tableElevation}`"
                                v-model="focus"
                                color="primary"
                                :events="events"
                                :event-color="getEventColor"
                                :type="type"
                                local="fr-fr"
                                @click:event="showEvent"
                                @click:more="viewDay"
                                @click:date="viewDay"
                                @change="updateRange"
                                locale="fr-fr"
                            ></v-calendar>
                            <v-menu
                                v-model="selectedOpen"
                                :close-on-content-click="false"
                                :activator="selectedElement"

                                min-width="500"
                                max-width="500"
                                :position-x="clientX"
                                :position-y="clientY"
                            >
                                <v-card
                                    color="grey lighten-4"
                                    min-width="500"
                                    max-width="500"
                                    flat
                                >
                                    <v-toolbar
                                        :color="selectedEvent.color"
                                        dark
                                        flat
                                        dense
                                    >
                                        <v-toolbar-title v-html="selectedEvent.name"></v-toolbar-title>
                                    </v-toolbar>
                                    <v-simple-table dense>
                                        <template v-slot:default>
                                            <tbody>
                                            <tr>
                                                <td>{{ $t("common.code") }}</td>
                                                <td class="text-right">
                                                    <v-chip
                                                        dark
                                                        label
                                                        color="teal"
                                                        small
                                                        :class="`ma-1 elevation-${chipElevation}`"
                                                    >
                                                        <strong>
                                                            {{ selectedEvent.workerCode }}
                                                        </strong>
                                                    </v-chip>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{{ $t("module.worker") }}</td>
                                                <td class="text-right">
                                                    <v-chip
                                                        dark
                                                        label
                                                        color="blue"
                                                        small
                                                        :class="`ma-1 elevation-${chipElevation}`"
                                                    >
                                                        {{ selectedEvent.workerName }}
                                                    </v-chip>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{{ $t("absence_request.start_date") }}</td>
                                                <td class="text-right">
                                                    <v-chip label small outlined color="teal">
                                                        <strong>
                                                            {{ selectedEvent.startDate | dateFormat }}
                                                        </strong>
                                                    </v-chip>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{{ $t("absence_request.end_date") }}</td>
                                                <td class="text-right">
                                                    <v-chip label small outlined color="red">
                                                        <strong>
                                                            {{ selectedEvent.endDate | dateFormat }}
                                                        </strong>
                                                    </v-chip>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{{ $t("module.absence_reason") }}</td>
                                                <td class="text-right">
                                                    <v-chip label small outlined color="purple">
                                                        <strong>
                                                            {{ selectedEvent.reasonName }}
                                                        </strong>
                                                    </v-chip>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{{ $t("common.days") }}</td>
                                                <td class="text-right">
                                                    <v-chip small label outlined>
                                                        <strong>
                                                            {{ selectedEvent.days }}
                                                        </strong>
                                                    </v-chip>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{{ $t("common.hours") }}</td>
                                                <td class="text-right">
                                                    <v-chip small label outlined>
                                                        <strong>
                                                            {{ selectedEvent.hours }}
                                                        </strong>
                                                    </v-chip>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{{ $t("absence_request.input_mode") }}</td>
                                                <td class="text-right">
                                                    <v-chip small label outlined>
                                                        <strong>
                                                            {{ $t(`absence_request.${selectedEvent.inputMode}`) }}
                                                        </strong>
                                                    </v-chip>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{{ $t("common.state") }}</td>
                                                <td class="text-right">
                                                    <v-chip
                                                        class="ma-1 elevation-15"
                                                        label
                                                        outlined
                                                        small
                                                    >
                                                        <strong>
                                                            {{ selectedEvent.validationStatus.lastState }}
                                                        </strong>
                                                    </v-chip>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{{ $t("common.state_date") }}</td>
                                                <td class="text-right">
                                                    <v-chip
                                                        v-if="selectedEvent.validationStatus.lastValidationDate"
                                                        class="ma-1 elevation-15"
                                                        label
                                                        outlined
                                                        small
                                                    >
                                                        <strong>
                                                            {{
                                                                selectedEvent.validationStatus.lastValidationDate | dateFormat
                                                            }}
                                                        </strong>
                                                    </v-chip>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{{ $t("common.next_validator") }}</td>
                                                <td class="text-right">
                                                    <v-chip
                                                        v-if="selectedEvent.validationStatus.nextValidator"
                                                        class="ma-1 elevation-15"
                                                        label
                                                        outlined
                                                        small
                                                    >
                                                        <strong>
                                                            {{ selectedEvent.validationStatus.nextValidator }}
                                                        </strong>
                                                    </v-chip>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </template>
                                    </v-simple-table>
                                    <v-divider/>
                                    <v-card-actions>
                                        <v-spacer/>
                                        <v-btn
                                            icon
                                            v-if="selectedEvent.validateOption"
                                            @click="$router.push({ name: `absence_requests_calendar.set_decision`, params: { id: selectedEvent.id, module: 'ABSENCE_REQUEST'},})"
                                        >
                                            <v-icon v-text="'mdi-arrow-right-thick'" color="deep-orange"></v-icon>
                                        </v-btn>
                                        <v-btn
                                            icon
                                            @click="$router.push({ name: `absence_requests_calendar.timelines`, params: { id: selectedEvent.id, module: 'ABSENCE_REQUEST'},})"
                                        >
                                            <v-icon v-text="'mdi-chart-timeline'" color="blue"></v-icon>
                                        </v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-menu>
                        </v-sheet>
                    </v-col>
                </v-row>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>

<script>

import {onMounted, ref, watch} from "vue";
import useTools from "@/use/Tools.js";
import useSharedData from "@/use/SharedData";
import {useAbsenceRequestStore} from "@/stores/AbsenceRequest";
import AbsenceRequestFilter from "@/views/Absence/AbsenceRequest/Components/AbsenceRequestFilter.vue";
import axios from "axios";
import useSharedMethod from "@/use/SharedMethod";

export default {
    props: {
        // vacation: Boolean,
    },
    components: {AbsenceRequestFilter},
    setup() {
        const {$refs, $i18n} = useTools();
        const {transformFilterValues} = useSharedMethod();
        const {loading, tableElevation, total, filter, chipElevation, rightDrawer} = useSharedData();
        const requestStore = useAbsenceRequestStore();

        const focus = ref('');
        const type = ref('month');
        const typeToLabel = ref({
            month: $i18n.t('common.month'),
            week: $i18n.t('common.week'),
            day: $i18n.t('common.day'),
            '4day': $i18n.t('common.4day'),
        });
        const selectedEvent = ref({});
        const selectedElement = ref(null);
        const selectedOpen = ref(false);
        // const events = ref([]);
        const colors = ref(['blue', 'indigo', 'deep-purple', 'cyan', 'green', 'orange', 'grey darken-1']);
        const names = ref(['Meeting', 'Holiday', 'PTO', 'Travel', 'Event', 'Birthday', 'Conference', 'Party']);

        function viewDay({date}) {
            focus.value = date
            type.value = 'day'
        }

        function getEventColor(event) {
            return event.color
        }

        function setToday() {
            focus.value = ''
        }

        function prev() {
            $refs.calendar.prev()
        }

        function next() {
            $refs.calendar.next()
        }

        const clientX = ref(0);
        const clientY = ref(0);

        function showEvent({nativeEvent, event}) {
            clientX.value = nativeEvent.clientX;
            clientY.value = nativeEvent.clientY;
            const open = () => {
                selectedEvent.value = event
                selectedElement.value = nativeEvent.target
                requestAnimationFrame(() => requestAnimationFrame(() => selectedOpen.value = true))
            }

            if (selectedOpen.value) {
                selectedOpen.value = false
                requestAnimationFrame(() => requestAnimationFrame(() => open()))
            } else {
                open()
            }

            nativeEvent.stopPropagation()
        }

        /*function rnd(a, b) {
          return Math.floor((b - a + 1) * Math.random()) + a;
        }*/

        const startDate = ref(null);
        const endDate = ref(null);

        const events = ref([]);

        function findAll() {
            loading.value = true;
            filter.value.start = startDate.value;
            filter.value.end = endDate.value;
            filter.value.allRecords = true;
            // filter.value.balanceId = securityStore.userContext.vacationBalanceId;
            axios.get('/absenceRequests',{params: transformFilterValues(filter.value)}).then(response => {
                requestStore.setAbsenceRequests(response.data.elements);
                total.value = response.data.elements;
                events.value = [];
                response.data.elements.forEach(element => {
                    events.value.push({
                        ...element,
                        name: `${element.reasonName} - ${element.workerName}`,
                        start: element.startTime ? `${element.startDate} ${element.startTime}` : element.startDate,
                        end: element.endTime ? `${element.endDate} ${element.endTime}` : element.endDate,
                        color: element.reasonColor,
                        // timed: !allDay,
                    });
                });
            }).finally(() => {
                loading.value = false;
            });
        }

        /*const events = computed(() => {
            let result = [];
            requestStore.absenceRequests.forEach(element => {
                result.push({
                    ...element,
                    name: `${element.reasonName} - ${element.workerName}`,
                    start: element.startDate,
                    end: element.endDate,
                    color: element.reasonColor,
                    // timed: !allDay,
                });
            });
            return result;
        });*/

        function updateRange({start, end}) {
            startDate.value = start.date;
            endDate.value = end.date;
            findAll();
        }

        function clearFilter() {
            Object.keys(filter.value).forEach(key => {
                filter.value[key] = null;
            });
        }

        watch(() => filter.value, () => {
                findAll();
            }, {deep: true}
        );

        onMounted(() => {
            $refs.calendar.checkChange();
        });

        return {
            focus,
            type,
            typeToLabel,
            selectedEvent,
            selectedElement,
            selectedOpen,
            events,
            colors,
            names,
            total,
            tableElevation,
            filter,
            loading,
            chipElevation,
            clientX,
            clientY,
            rightDrawer,
            viewDay,
            getEventColor,
            setToday,
            prev,
            next,
            showEvent,
            updateRange,
            clearFilter,
        }
    },
};
</script>
